import React, { useCallback, useMemo, useState } from 'react';
import { logError, logUsage } from '../../../../services/eventLog';
import { useDispatch, useSelector } from 'react-redux';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import { resolveEndpoint } from '../../utils/transformLoad';
import { suggestQualityRules, RuleSuggestion, ruleLabel } from '../../mapping/suggestQualityRules';
import { be1Fetch } from '../../../../config/api';
import { useTranslation } from 'react-i18next';

interface Props { node: any }

/** [규칙, 값 입력이 있는가] — 표시 이름·예시는 property:rule.<규칙> / <규칙>Hint 번역 키 */
const RULES: Array<[string, boolean]> = [
  ['not_null', false], ['unique', false], ['range', true], ['length', true], ['in_list', true], ['regex', true], ['expr', true],
];

/** 데이터 품질 검사 속성 — 규칙 표(항목·규칙·값·메시지), 위반 처리 방식, 허용 위반 비율 */
const QualityNodePanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  // useMemo 로 고정한다 — 매 렌더 새 객체가 되면 아래 useCallback 들이 계속 새로 만들어진다
  const cfg = useMemo(() => node?.data?.config || {}, [node]);
  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);
  const upstream = useMemo(() => resolveEndpoint(node.id, 'up', nodes, edges), [node.id, nodes, edges]);
  const cols = useMemo(() => (upstream?.columns || []).map((c) => c.name), [upstream]);
  const rules: any[] = useMemo(() => cfg.rules || [], [cfg]);
  const setRule = (i: number, p: any) => patch({ rules: rules.map((r, k) => (k === i ? { ...r, ...p } : r)) });
  const inp = 'border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white';

  // 규칙 제안 — 상류 데이터의 표본을 읽어 값에서 규칙을 뽑는다(LLM 없이 결정적으로).
  const [proposals, setProposals] = useState<RuleSuggestion[] | null>(null);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestErr, setSuggestErr] = useState('');

  /**
   * 표본을 읽어 올 데이터 노드를 고른다.
   * 품질검사 앞이 내부데이터면(추출 → 내부데이터 → 품질검사) 값이 어디에도 저장돼 있지 않으므로,
   * 그 내부데이터를 만든 태스크의 **원천**까지 거슬러 올라가 거기서 표본을 읽는다.
   * 다만 그 태스크가 항목을 바꿨을 수 있으니, 표본의 항목명이 실제로 겹칠 때만 쓴다(아래 검사).
   */
  const sampleSource = useMemo(() => {
    if (!upstream) return null;
    if (upstream.kind !== 'inner') return upstream;
    const feeder = edges.filter((e: any) => e.target === upstream.node.id)
      .map((e: any) => nodes.find((n: any) => n.id === e.source)).find(Boolean);
    return feeder ? resolveEndpoint(feeder.id, 'up', nodes, edges) : null;
  }, [upstream, nodes, edges]);

  const suggest = useCallback(async () => {
    if (!upstream) return;
    setSuggestBusy(true); setSuggestErr(''); setProposals(null);
    let sample: Array<Record<string, unknown>> = [];
    try {
      if (sampleSource && sampleSource.kind !== 'inner') {
        const c = sampleSource.node.data?.config || {};
        const body = sampleSource.kind === 'db'
          ? { kind: 'db', connection: c.connection, schema: c.schema, table: c.table, limit: 100 }
          : { kind: 'csv', filepath: c.filepath, delimiter: c.delimiter || ',', hasHeader: c.hasHeader !== false, fieldNames: c.fieldNames || undefined, encoding: c.encoding || 'UTF-8', limit: 100 };
        const r = await be1Fetch('/api/data/preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
        if (r.ok) sample = (await r.json()).rows || [];
      }

      // 검사할 항목: 상류가 알려 준 것, 없으면 표본의 항목명
      let columns = upstream.columns || [];
      const sampleKeys = sample.length ? Object.keys(sample[0]) : [];
      if (!columns.length && sampleKeys.length) columns = sampleKeys.map((name) => ({ name }));

      // 표본이 다른 항목 구성이면(중간 태스크가 이름을 바꿨다) 값 기반 판정을 믿을 수 없다
      const overlap = columns.filter((c) => sampleKeys.includes(c.name)).length;
      if (sample.length && columns.length && overlap === 0) {
        sample = [];
        setSuggestErr(t('property:q.renamedUpstream'));
      }

      const s = suggestQualityRules(columns, sample, rules);
      logUsage('quality.suggest', { detail: { columns: columns.length, sample: sample.length, proposed: s.length } });
      setProposals(s);
      setPicked(Object.fromEntries(s.map((x, i) => [i, !x.weak])));
      if (!s.length && !suggestErr) {
        setSuggestErr(sample.length ? t('property:q.noRulesFromSample') : t('property:q.noSample'));
      }
    } catch (e: any) {
      logError('quality.suggest', e, { where: 'suggestQualityRules' });
      setSuggestErr(e?.message || t('property:q.sampleFailed'));
    } finally {
      setSuggestBusy(false);
    }
  }, [upstream, sampleSource, rules, suggestErr, t]);

  const applyProposals = () => {
    const add = (proposals || []).filter((_, i) => picked[i])
      .map((s) => ({ column: s.column, rule: s.rule, param: s.param, message: s.message }));
    if (add.length) patch({ rules: [...rules, ...add] });
    setProposals(null);
  };

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-500">{t('property:an.nodeType')}</span>
          <input type="text" value={t('property:q.typeLabel')} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} /></div>
      </div>
      <div className="mb-3"><label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} /></div>
      <div className="mb-2 text-[0.6875rem] text-gray-500">{t('property:q.input', { source: upstream ? `${upstream.kindLabel} · ${upstream.name}` : t('property:q.noUpstream') })}<code>_violations</code>{t('property:q.inputTail')}</div>

      <div className="mb-3 rounded border border-gray-200 p-2">
        <div className="text-xs font-semibold text-gray-600 mb-1">{t('property:q.rules')}</div>
        {rules.length === 0 && <div className="text-xs text-gray-400 mb-1">{t('property:q.noRules')}</div>}
        {rules.map((r, i) => {
          const def = RULES.find((x) => x[0] === r.rule) || RULES[0];
          return (
            <div key={i} className="mb-1.5 rounded border border-gray-100 p-1.5">
              <div className="flex items-center gap-1 mb-1">
                {cols.length ? (
                  <select className={`${inp} w-[36%]`} value={r.column || ''} onChange={(e) => setRule(i, { column: e.target.value })}>
                    <option value="">{t('property:q.columnPlaceholder')}</option>{cols.map((c) => <option key={c} value={c}>{c}</option>)}{r.column && !cols.includes(r.column) && <option value={r.column}>{r.column}</option>}
                  </select>
                ) : <input className={`${inp} w-[36%] font-mono`} value={r.column || ''} placeholder={t('property:q.column')} onChange={(e) => setRule(i, { column: e.target.value })} />}
                <select className={`${inp} flex-1`} value={r.rule} onChange={(e) => setRule(i, { rule: e.target.value })}>{RULES.map(([v]) => <option key={v} value={v}>{t(`property:rule.${v}`)}</option>)}</select>
                <button className="text-gray-400 hover:text-red-500" onClick={() => patch({ rules: rules.filter((_, k) => k !== i) })}>✕</button>
              </div>
              {def[1] && <input className={`${inp} w-full font-mono mb-1`} value={r.param || ''} placeholder={t(`property:rule.${def[0]}Hint`)} onChange={(e) => setRule(i, { param: e.target.value })} />}
              <input className={`${inp} w-full`} value={r.message || ''} placeholder={t('property:q.messagePlaceholder')} onChange={(e) => setRule(i, { message: e.target.value })} />
            </div>
          );
        })}
        <div className="flex items-center gap-1">
          <button className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100" onClick={() => patch({ rules: [...rules, { column: cols[0] || '', rule: 'not_null', param: '', message: '' }] })}>{t('property:q.addRule')}</button>
          <button className="px-2 py-0.5 text-xs rounded border border-blue-400 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            disabled={!upstream || suggestBusy} onClick={suggest}
            title={sampleSource && sampleSource.kind !== 'inner'
              ? t('property:q.sampleSource', { kind: sampleSource.kindLabel, name: sampleSource.name })
              : t('property:q.noSampleSource')}>
            {suggestBusy ? t('property:q.readingSample') : t('property:q.suggest')}
          </button>
        </div>

        {suggestErr && <div className="mt-1 text-[0.6875rem] text-gray-500">{suggestErr}</div>}

        {proposals && proposals.length > 0 && (
          <div className="mt-2 rounded border border-blue-200 bg-blue-50/60 p-2">
            <div className="mb-1 text-[0.6875rem] text-blue-800">
              {t('property:q.proposals', { n: proposals.length })}
            </div>
            <div className="max-h-56 overflow-auto">
              {proposals.map((s, i) => (
                <label key={`${s.column}:${s.rule}`} className="mb-1 flex items-start gap-1.5 text-[0.6875rem] text-gray-700">
                  <input type="checkbox" className="mt-0.5" checked={!!picked[i]}
                    onChange={(e) => setPicked((p) => ({ ...p, [i]: e.target.checked }))} />
                  <span>
                    <b className="font-mono">{s.column}</b> · {ruleLabel(s.rule)}
                    {s.param && <span className="font-mono text-gray-600"> ({s.param})</span>}
                    {s.weak && <span className="ml-1 rounded bg-amber-100 px-1 text-amber-800">{t('property:q.needsReview')}</span>}
                    <span className="block text-gray-500">{s.evidence}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-1 flex gap-1">
              <button className="rounded bg-blue-600 px-2 py-0.5 text-[0.6875rem] text-white hover:bg-blue-700"
                onClick={applyProposals}>{t('property:q.addSelected')}</button>
              <button className="rounded border border-gray-300 px-2 py-0.5 text-[0.6875rem] text-gray-600 hover:bg-gray-100"
                onClick={() => setProposals(null)}>{t('property:q.close')}</button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-3 rounded border border-gray-200 p-2">
        <div className="text-xs font-semibold text-gray-600 mb-1">{t('property:q.onViolation')}</div>
        <div className="flex flex-col gap-1 text-xs text-gray-700">
          <label className="flex items-center gap-1.5"><input type="radio" checked={(cfg.onViolation || 'reject') === 'reject'} onChange={() => patch({ onViolation: 'reject' })} />{t('property:q.reject')}</label>
          <label className="flex items-center gap-1.5"><input type="radio" checked={cfg.onViolation === 'fail'} onChange={() => patch({ onViolation: 'fail' })} />{t('property:q.fail')}</label>
          <label className="flex items-center gap-1.5"><input type="radio" checked={cfg.onViolation === 'warn'} onChange={() => patch({ onViolation: 'warn' })} />{t('property:q.warn')}</label>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-700 mt-1.5">{t('property:q.maxPct')}
          <input type="number" min={0} max={100} className={`${inp} w-20`} value={cfg.maxViolationPct ?? ''} placeholder={t('property:q.noLimit')} onChange={(e) => patch({ maxViolationPct: e.target.value === '' ? '' : Number(e.target.value) })} />
          <span className="text-gray-400">{t('property:q.failAbove')}</span>
        </label>
      </div>
    </div>
  );
};

export default QualityNodePanel;
